import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ResultsBaseService } from '@comparit/calculator';
import { selectControlValue } from '@comparit/core';
import { map } from 'rxjs';
import { Sparte, Zahlungsweise } from '../interfaces';
import type { HausratBerechnungvorgaben, SachErgebnis } from '../interfaces';
import { HausratRoutes } from '../app/app.routes.paths';
import { FormsService } from './forms.service';
import { TOTAL_STEPS } from './step-init.service';
import { VersichererCacheService } from './versicherer-cache.service';
import { beitragWording } from './vertragsdaten/options/zahlungsweise-wording';

/** Hausrat's results engine: the shared hub client and trigger, fed by this app's funnel. */
@Injectable({ providedIn: 'root' })
export class ResultsService extends ResultsBaseService<HausratBerechnungvorgaben, SachErgebnis> {
  private readonly formsService = inject(FormsService);
  private readonly versichererCache = inject(VersichererCacheService);

  protected readonly sparte = Sparte.Hr;
  protected readonly vorgaben$ = this.formsService.berechnungFormularValue$;
  protected readonly allFgs = this.formsService.allFgs;
  readonly calculatorForm = this.formsService.calculatorForm;

  readonly formularUrl = HausratRoutes.formular;
  readonly backUrl = HausratRoutes.step(TOTAL_STEPS);
  override readonly forwardUrl = HausratRoutes.vergleich;

  private readonly zahlungsweiseCtrl =
    this.formsService.vertragsdatenFormCreator.step2Form.ctrls.zahlungsweise;
  private readonly zahlungsweise = toSignal(selectControlValue(this.zahlungsweiseCtrl), {
    initialValue: this.zahlungsweiseCtrl.value,
  });

  /**
   * Read off the request, not off `ergebnis.zahlungsweise`: the hub serializes that field as its
   * raw number rather than the string the OpenAPI contract promises (kfz carries the same
   * workaround), and every result of a run was priced for the same Zahlungsweise anyway.
   */
  readonly beitragCaption = computed(
    () => beitragWording(this.zahlungsweise() ?? Zahlungsweise.Jaehrlich).caption
  );

  readonly findVersichererLogo$ = (bafinRegNr: string) =>
    this.versichererCache
      .findVersicherer$(bafinRegNr)
      .pipe(map(versicherer => versicherer?.logo ?? undefined));
}
